import { Schema } from "effect";
import { PreviewResult, HostedResult } from "../../api/execution/model.ts";

export const ExecutionTarget = Schema.Struct({ workspaceId: Schema.String, stationId: Schema.String });
export type ExecutionTarget = typeof ExecutionTarget.Type;
export const ExecutionInput = Schema.Struct({
  target: Schema.NullOr(ExecutionTarget), files: Schema.Record(Schema.String, Schema.String), path: Schema.String,
  booted: Schema.Boolean, hydrationSequence: Schema.Int, freshPreviewSequence: Schema.Int
});
export type ExecutionInput = typeof ExecutionInput.Type;
export const ExecutionError = Schema.Struct({ message: Schema.String });
export const ExecutionSnapshot = Schema.Struct({
  selectionKey: Schema.String, target: Schema.NullOr(ExecutionTarget), closed: Schema.Boolean,
  running: Schema.Boolean, result: Schema.NullOr(PreviewResult), error: Schema.NullOr(ExecutionError),
  testing: Schema.Boolean, testResult: Schema.NullOr(HostedResult), testError: Schema.NullOr(ExecutionError),
  runId: Schema.Int, workerVersion: Schema.Int, consoleSource: Schema.Literals(["worker", "test"]),
  watchMode: Schema.Boolean, testWatch: Schema.Boolean, inFileConcurrency: Schema.Boolean
});
export type ExecutionSnapshot = typeof ExecutionSnapshot.Type;
export const emptyExecutionSnapshot = (): ExecutionSnapshot => ({
  selectionKey: "", target: null, closed: false, running: false, result: null, error: null,
  testing: false, testResult: null, testError: null, runId: 0, workerVersion: 0,
  consoleSource: "worker", watchMode: false, testWatch: false, inFileConcurrency: false
});
export const executionSelectionKey = (input: ExecutionInput): string => input.target === null || !input.booted ? ""
  : JSON.stringify([input.target.stationId, input.target.workspaceId, input.hydrationSequence]);
export const hasTestFiles = (files: Readonly<Record<string, string>>): boolean => Object.keys(files).some(file => /(?:^|\/)(?:[^/]+\.)?(?:test|spec)\.(?:[cm]?[jt]sx?)$/i.test(file));
export interface PreviewInit {
  readonly path?: string;
  readonly method?: string;
  readonly headers?: readonly (readonly [string, string])[];
  readonly body?: string | null;
}
export type ExecutionAction =
  | { readonly _tag: "Receive"; readonly input: ExecutionInput }
  | { readonly _tag: "Preview"; readonly selectionKey: string; readonly init?: PreviewInit }
  | { readonly _tag: "Tests"; readonly selectionKey: string }
  | { readonly _tag: "WatchPreview"; readonly enabled: boolean }
  | { readonly _tag: "WatchTests"; readonly enabled: boolean }
  | { readonly _tag: "Parallel"; readonly enabled: boolean }
  | { readonly _tag: "Console"; readonly source: "worker" | "test" };
export interface ExecutionSettlement {
  readonly id: number;
  readonly kind: "preview" | "tests";
  readonly target: ExecutionTarget;
  readonly outcome: "success" | "failure";
  readonly result: PreviewResult | HostedResult | null;
  readonly message: string | null;
  readonly presented: boolean;
}

export type ExecutionCommand = Exclude<ExecutionAction, { readonly _tag: "Receive" | "Preview" | "Tests" }>
  | { readonly _tag: "Preview"; readonly init?: PreviewInit }
  | { readonly _tag: "Tests" };
